import styled, { keyframes } from 'styled-components';

import { Text } from '@/nova/components';
import { CFG, CFG_ANIM } from '../../../../../../theme/configurator-tokens';
import { FINISH } from '../../finish.tokens';

/* `$inset` aligns the heading + input with the billing rows above, which are indented by their
   container's padding: 16px on desktop (card + row), 8px in the mobile summary (row only). */
export const Root = styled.div<{ $inset: number }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 ${({ $inset }) => $inset}px;
`;

export const Title = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: ${CFG.textDim};
`;

/* Collapsed state: a quiet text link in the Title's slot (same size + weight, brand ink) so the
   review keeps its rhythm with the field folded away. */
export const RevealLink = styled.button`
  align-self: flex-start;
  border: none;
  background: none;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: ${CFG.brandText};
  cursor: pointer;
  transition: opacity ${CFG_ANIM.INTERACT_MS}ms ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  &:focus-visible {
    outline: 2px solid ${CFG.brandText};
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const revealIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* Expanded state: the input row (+ message) fades in under the heading, short and subtle. */
export const Reveal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${revealIn} ${CFG_ANIM.POPUP_MS}ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const InputRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

/* Matches the checkout EmailInput (PaymentChrome.styles) so the field reads as part of the same
   payment chrome: transparent fill, 2px border that brightens on focus. */
export const Input = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border-radius: 12px;
  border: 2px solid ${FINISH.inputBorder};
  background: transparent;
  padding: 10px 14px;
  font-size: 15px;
  line-height: 20px;
  color: ${FINISH.inputText};

  &::placeholder {
    color: ${FINISH.inputPlaceholder};
  }
  &:focus {
    border-color: ${FINISH.inputBorderFocus};
    outline: none;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ApplyButton = styled.button`
  flex: 0 0 auto;
  border: none;
  border-radius: 12px;
  padding: 0 18px;
  background: ${CFG.brand};
  color: ${FINISH.payButtonText};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition:
    filter 120ms ease,
    opacity 120ms ease;

  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const Status = styled(Text)<{ $tone: 'success' | 'error' }>`
  font-size: 13px;
  line-height: 1.35;
  color: ${({ $tone }) => ($tone === 'error' ? FINISH.errorText : CFG.success)};
`;
